import { useCallback, useEffect, useRef } from 'react'

import type { ProductDataVariant } from '../../../../services/product/product-data'
import {
  createDataLayerEcommerceProduct,
  DataLayerEcommerceProduct,
  pushDataLayerProductListVariantChange,
  pushDataLayerProductListVariantClick,
  pushDataLayerProductListView
} from '../../common/datalayer/product'
import type { GroupedItem } from '../models'
import { ProductListObserver } from '../utils'
import { PRODUCT_CARD_ID_PREFIX } from '../utils/constants'

interface UseAnalyticsParams {
  sortedItemsByPrice: GroupedItem[] | undefined
  dataLayerListName: string | undefined
  isSsr: boolean
  containerRef: React.RefObject<HTMLElement | null>
}

export const useAnalytics = ({
  sortedItemsByPrice,
  dataLayerListName,
  isSsr,
  containerRef
}: UseAnalyticsParams) => {
  const productIDs = useRef<string[]>([])
  const queue = useRef<DataLayerEcommerceProduct[]>([])

  const onVariantLink = useCallback(
    (
      variant: Partial<ProductDataVariant>,
      url: string,
      { list, position }: { list: string; position: number }
    ) => {
      const dataLayerEcommerceProduct = createDataLayerEcommerceProduct(variant)

      pushDataLayerProductListVariantClick(
        {
          ...dataLayerEcommerceProduct,
          list,
          position
        },
        url
      )
    },
    []
  )

  const onVariantColorChange = useCallback(
    (
      variant: Partial<ProductDataVariant>,
      { list, position }: { list: string; position: number }
    ) => {
      const dataLayerEcommerceProduct = createDataLayerEcommerceProduct(variant)

      pushDataLayerProductListVariantChange({
        ...dataLayerEcommerceProduct,
        list,
        position
      })
    },
    []
  )

  // IntersectionObserver for product list view tracking
  useEffect(() => {
    const container = containerRef.current

    if (!isSsr && sortedItemsByPrice?.length && container) {
      const products = sortedItemsByPrice
      const len = 15

      const spyPage = (entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry) => {
          const id = String(entry.target.getAttribute('id'))
          const position = entry.target.getAttribute('data-position')

          if (
            entry.isIntersecting &&
            position &&
            !productIDs.current.includes(id)
          ) {
            productIDs.current.push(id)

            const index = +position - 1
            const variant = products[index].variants[0]

            const dataLayerEcommerceProduct =
              createDataLayerEcommerceProduct(variant)
            queue.current.push({
              ...dataLayerEcommerceProduct,
              list: dataLayerListName,
              position
            })

            if (queue.current.length >= len) {
              pushDataLayerProductListView(queue.current)
              queue.current = []
            }
          }
        })
      }

      const observer = new ProductListObserver({
        container,
        selector: `[id*="${PRODUCT_CARD_ID_PREFIX}"]`,
        onLoaded: spyPage,
        onScroll: spyPage,
        onInterval() {
          if (queue.current.length) {
            pushDataLayerProductListView(queue.current)
            queue.current = []
          }
        },
        onBeforeUnload() {
          if (queue.current.length) {
            pushDataLayerProductListView(queue.current)
          }
        }
      })

      return () => {
        productIDs.current = []
        queue.current = []

        observer.disconnect()
      }
    }

    return () => {}
  }, [dataLayerListName, sortedItemsByPrice, isSsr]) // eslint-disable-line react-hooks/exhaustive-deps

  return { onVariantLink, onVariantColorChange }
}
